import React, { useState } from 'react';
import { TextField, Button, MenuItem, FormControl, InputLabel, Select, Typography, Grid, CircularProgress, IconButton } from '@material-ui/core';
import NeokodeButton from '../../components/NeokodeButton';
import { Link as RouterLink } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import * as Icons from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import user_access from '../../data/customer_user_access';
import { invokeApig } from '../../lib/awsLib';
import { validateEmail, validateName } from '../../lib/validate';

const useStyles = makeStyles((theme) => ({
  formControl: {
    //margin: theme.spacing(1),
    minWidth: 120,
  },
  button: {
    margin: theme.spacing(1),
  },
  title: {
    margin: theme.spacing(1),
    textAlign: 'center',
  },
  paper: {
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0),
  },
  accessTitle: {
    marginTop: '40px',
  },
  buttonGoBack: {
    marginTop: '15px',
  },
  lookupContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  infoAlert: {
    marginTop: theme.spacing(2),
  },
}));

// Flujo "email primero": se busca el correo antes de mostrar el formulario y se
// ramifica segun el resultado del lookup, sin callejones sin salida.
// MODE: 'create' (no existe), 'assign' (existe, no en esta empresa), 'edit' (ya en esta empresa)
function CustomerUserForm(props) {
  const classes = useStyles();
  const readOnly = false;
  const id = props.id;
  const isEditFromList = !!props.user;
  const [step, setStep] = useState(isEditFromList ? 'form' : 'email');
  const [mode, setMode] = useState(isEditFromList ? 'edit' : null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [user, setUser] = useState({
    email: props.user ? props.user.email : '',
    name: props.user ? props.user.name : '',
    lastName: props.user ? props.user.lastName : '',
    phoneNumber: props.user ? props.user.phoneNumber : '',
    status: props.user ? props.user.status : '',
    enabled: props.user ? props.user.enabled : false,
    user_access: props.user ? props.user.user_access : {},
  });

  const createUser = (user) => {
    return invokeApig({
      path: `/account/user`,
      method: "POST",
      body: user
    });
  };

  const updateUser = (user) => {
    return invokeApig({
      path: `/account/user`,
      method: "PUT",
      body: user
    });
  };

  const handleLookup = async (event) => {
    event.preventDefault();
    if (!validateEmail(user.email)) {
      return;
    }
    setIsError(false);
    setIsLookingUp(true);
    try {
      const response = await invokeApig({
        path: `/account/user/lookup`,
        method: "POST",
        body: { email: user.email, owner: id }
      });
      if (response.code === 0 && response.data) {
        if (!response.data.exists) {
          //no existe en la plataforma: creacion normal
          setMode('create');
        } else {
          //existe: se precargan sus datos conocidos
          setUser((prev) => ({
            ...prev,
            name: response.data.user.name || '',
            lastName: response.data.user.lastName || '',
            phoneNumber: response.data.user.phoneNumber || '',
            enabled: response.data.user.enabled,
            user_access: response.data.user_access || {},
          }));
          setMode(response.data.belongsToCompany ? 'edit' : 'assign');
        }
        setStep('form');
      } else {
        setMessage('No pudimos verificar el correo. Intenta nuevamente o comunícate con Soporte');
        setIsError(true);
      }
    } catch (error) {
      console.error('Error in lookup:', error);
      setMessage('No pudimos verificar el correo. Intenta nuevamente o comunícate con Soporte');
      setIsError(true);
    }
    setIsLookingUp(false);
  };

  const handleChangeEmail = (event) => {
    setStep('email');
    setMode(null);
    setIsError(false);
    setMessage('');
    setUser((prev) => ({
      ...prev,
      name: '',
      lastName: '',
      phoneNumber: '',
      enabled: false,
      user_access: {},
    }));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setUser({ ...user, [name]: value });
  };

  const handleChangeAccess = (event) => {
    const { name, value } = event.target;
    const attr = name.split('_');
    const user_access = { ...user.user_access, [attr[1]]: [value] };
    setUser({ ...user, user_access: user_access });
  }

  const validate = () => {
    let message = undefined;
    if (!validateEmail(user.email)) {
      message = 'Debes ingresar el email correctamente';
    } else {
      if (!validateName(user.name)) {
        message = 'Debes ingresar el nombre correctamente';
      } else {
        if (!validateName(user.lastName)) {
          message = 'Debes ingresar el apellido correctamente';
        }
      }
    }
    return message;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    const errorMsg = validate();
    if (errorMsg) {
      setMessage(errorMsg);
      setIsError(true);
      setIsLoading(false);
      return;
    }
    const new_user = {
      email: user.email,
      name: user.name,
      lastName: user.lastName,
      phoneNumber: !!user.phoneNumber ? user.phoneNumber : '',
      status: user.status,
      enabled: user.enabled,
      user_access: user.user_access,
      owner: id
    };
    if (mode === 'edit') {
      const response = await updateUser(new_user);
      if (response.errorMessage || response.code !== 0) {
        setMessage('Error al modificar el usuario, por favor revisa los datos e intenta nuevamente');
        setIsError(true);
      } else {
        setMessage('El usuario se ha modificado correctamente');
        setIsSuccess(true);
      }
    } else { //create o assign
      const response = await createUser(new_user);
      if (response.errorMessage) {
        setMessage('Error al crear el usuario, por favor revisa los datos e intenta nuevamente');
        setIsError(true);
      } else if (response.code === 0) {
        setMessage('El usuario se ha creado correctamente');
        setIsSuccess(true);
      } else if (response.code === 1) {
        setMessage('El usuario fue agregado a esta empresa correctamente');
        setIsSuccess(true);
      } else if (response.code === 2) {
        setMessage('El usuario ya pertenece a esta empresa');
        setIsError(true);
      } else {
        setMessage('Error al crear el usuario, por favor revisa los datos e intenta nuevamente');
        setIsError(true);
      }
    }
    setIsLoading(false);
  };

  const handleGoBack = (event) => {
    event.preventDefault();
    if (props.onFinish) {
      props.onFinish(false);
    } else {
      props.history.push(`/customers/${id}/users`);
    }
  }

  if (isSuccess) {
    return (
      <>
        <Alert severity="success">{message}</Alert>
        <NeokodeButton variant="contained" component={RouterLink} to={`/customers/${id}/users`} className={classes.buttonGoBack}>Volver</NeokodeButton>
      </>
    );
  }

  // Paso 1: solo el correo + buscar
  if (step === 'email') {
    return (
      <form onSubmit={handleLookup}>
        <Typography>Ingresa el correo del usuario para comenzar</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <div className={classes.lookupContainer}>
              <TextField label="Email" name="email" value={user.email} onChange={handleChange} required fullWidth className={classes.formControl} autoFocus />
              <IconButton type="submit" color="primary" disabled={!validateEmail(user.email) || isLookingUp} aria-label="Buscar usuario">
                {isLookingUp ? <CircularProgress size={24} /> : <Icons.Search />}
              </IconButton>
            </div>
          </Grid>
        </Grid>
        {
          isError &&
          <Alert severity="error" className={classes.infoAlert}>
            {message}
          </Alert>
        }
        <Grid container justifyContent="flex-end">
          <Grid item>
            <Button
              type="button"
              variant="contained"
              className={classes.button}
              onClick={handleGoBack}
              disabled={isLookingUp}
            >
              Volver
            </Button>
          </Grid>
        </Grid>
      </form>
    );
  }

  const isAssign = mode === 'assign';
  const personReadOnly = readOnly || isAssign;

  return (
    <>
      <form onSubmit={handleSubmit}>
        {
          isAssign &&
          <Alert severity="info" className={classes.infoAlert}>
            Este usuario ya existe en la plataforma. Se agregará a esta empresa; solo define sus permisos.
          </Alert>
        }
        {
          !isEditFromList && mode === 'edit' &&
          <Alert severity="info" className={classes.infoAlert}>
            Este usuario ya pertenece a esta empresa. Puedes editar sus datos y permisos.
          </Alert>
        }
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField label="Email" name="email" value={user.email} required fullWidth className={classes.formControl} InputProps={{ readOnly: true, }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Teléfono" name="phoneNumber" value={user.phoneNumber} onChange={handleChange} fullWidth className={classes.formControl} placeholder="+56912345678" InputProps={{ readOnly: isAssign, }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Nombre" name="name" value={user.name} onChange={handleChange} required fullWidth className={classes.formControl} InputProps={{ readOnly: personReadOnly, }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Apellido" name="lastName" value={user.lastName} onChange={handleChange} required fullWidth className={classes.formControl} InputProps={{ readOnly: personReadOnly, }} />
          </Grid>
          {
            mode === 'edit' &&
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth className={classes.formControl}>
                  <InputLabel>Estado</InputLabel>
                  <Select name='enabled' value={user.enabled} onChange={handleChange} inputProps={{ readOnly: readOnly, }}>
                    <MenuItem value={true}>Activo</MenuItem>
                    <MenuItem value={false}>Inactivo</MenuItem>
                  </Select>
                </FormControl>
             </Grid>
            </>
          }
        </Grid>
        <Typography variant="h6" className={classes.accessTitle}>Permisos</Typography>
        <Grid container spacing={2}>
          {
            Object.keys(user_access).map((key, index) => {
              const access_obj = user_access[key];
              return (
                <Grid item xs={12} sm={6} key={index}>
                  <FormControl fullWidth className={classes.formControl}>
                    <InputLabel>{access_obj.name}</InputLabel>
                    <Select name={`access_${key}`} value={!!user && user.user_access[key] ? user.user_access[key] : ''} onChange={handleChangeAccess} inputProps={{ readOnly: readOnly, }}>
                      <MenuItem value="">Sin permiso</MenuItem>
                      {
                        access_obj.list.map((acc, index_access) => (
                          <MenuItem key={index_access} value={acc.value}>{acc.label}</MenuItem>
                        ))
                      }
                    </Select>
                  </FormControl>
                </Grid>
              );
            })
          }
        </Grid>
        {
          isError &&
          <Alert severity="error">
            {message}
          </Alert>
        }
        <Grid container justifyContent="flex-end">
          {
            !isEditFromList &&
            <Grid item>
              <Button
                type="button"
                variant="contained"
                className={classes.button}
                onClick={handleChangeEmail}
                disabled={isLoading}
              >
                Buscar otro correo
              </Button>
            </Grid>
          }
          <Grid item>
            <Button
              type="button"
              variant="contained"
              className={classes.button}
              onClick={handleGoBack}
              disabled={isLoading}
            >
              Volver
            </Button>
          </Grid>
          {
            !readOnly &&
            <Grid item>
              <NeokodeButton
                type="submit"
                variant="contained"
                className={classes.button}
                disabled={isLoading}
              >
                {
                  isLoading ?
                  <CircularProgress />
                  :
                  ( mode === 'edit' ? 'Editar Usuario' : (isAssign ? 'Agregar a esta empresa' : 'Crear Usuario') )
                }
              </NeokodeButton>
            </Grid>
          }
        </Grid>
      </form>
    </>
  );
}

export default CustomerUserForm;
